/**
 * 計画マスタ 所属発電所 編集 UI (発電販売計画マスタ専用)。
 *
 * gen-sales-plant-master-select 設計書: 発電所マスタからのドロップダウン選択に
 * 置換 (旧 free-text `plant_code` 入力を廃止)。 select 本体は DS `Select`
 * (= `~/components/common/FormSelect`、 2026-08-05 デザイナー確定)。
 *
 * 各行 = 発電所 (系統コード) select + 所属 BG select + 有効期間(validFrom/validTo)。
 * 候補 filter = 同一エリア(areaCode === selfAreaCode、AC-03)。
 * 計画区分があれば、今日の電源種別が区分に合う系統コード、今日は未定でも先の日付から有効なユニットが
 * 区分に合う系統コードに絞る (電源ユニットマスタ 2b / AC-9a。行の期間で種別が決まるかは保存時に backend が検査する)。
 * 同じ系統コードの行は期間が重ならない限り何行でも置けるので (所属 BG の付け替えは
 * 旧い行に適用終了 + 新しい行)、他の行で継続中 (適用終了が空欄) の系統コードだけを
 * 候補から除く。 閉じた行だけなら候補に出す (電源ユニットマスタ 2a。 重なりは保存の検査が止める)。
 * 所属 BG の候補 = 同じダイアログの BG 情報で選ばれている BG。 BG 情報から外した
 * BG を指したままの行は、選択中の表示はそのまま (注記は付けない) で、BG 情報から外した時点で所属 BG 欄に
 * 「系統コード {cd} の所属 BG が BG 情報にありません」を出す (入力中の検査。同じ BG を足し直せば消える。
 * 保存時の検査とサーバーの検査はそのまま)。
 * BG 情報が 1 つだけなら、追加した行の所属 BG の既定にする。
 */
import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type GenerationSaleSubKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  type Control,
  type FieldValues,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import type { PlantMasterListItemRow } from '~/hooks/useListPlantMasters'
import { todayCivilDate } from '~/models/civilDate'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import {
  type PlantUnitForCandidate,
  plantIsCandidateForPlanCategory,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/planCategory'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import { availableOptions } from '~/pages/AdminOcctoMaster/tabs/_common/models/availableOptions'
import { planMasterRowDefaultValidFrom } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type PlantEntry = {
  plantMasterId: string
  bgMasterId: string
  validFrom: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** i18n ページキー namespace */
  i18nNs: 'adminOcctoMaster.generationSalesPlanMaster'
  /**
   * 発電所マスタ候補一覧 (GetGenerationSalesPlanMasterFormOptions.plants、
   * AC-05: 区分フィルタなしの全件)。
   */
  plantMasters: PlantMasterListItemRow[]
  /** 計画提出者のエリアコード (同一エリアの発電所のみ候補に絞り込む、AC-03) */
  selfAreaCode: AreaCode | undefined
  /**
   * この計画マスタの計画区分。指定した時、発電所の候補は電源種別が決まっていて計画区分に合うものだけに
   * 絞る (電源ユニット 2b / AC-9a)。すでに行で選んでいる発電所は残す。
   */
  planCategory?: GenerationSaleSubKind
  /**
   * 組織の電源ユニット (ListPlantUnits)。今日は種別が未定でも、先の日付から有効なユニットがある系統コードを
   * 候補に出すのに使う (DE 判断 2026-10-28 B)。取得中・取得失敗は undefined (今日の値だけで判定する)。
   */
  plantUnits?: readonly PlantUnitForCandidate[]
  /**
   * 全 BG マスタ一覧 (所属 BG の表示名の引き当て専用)。 list クエリ取得中は undefined。
   * 引き当てられない BG は id をそのまま出す。
   */
  allBgMasters: BgMasterRow[] | undefined
}

type BgEntryOfForm = { bgMasterId: string }

export const PlanMasterPlantsSection = ({
  control,
  i18nNs,
  plantMasters,
  selfAreaCode,
  planCategory,
  plantUnits,
  allBgMasters,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'plants',
  })

  const rawWatchedPlants = useWatch({ control, name: 'plants' }) as
    | PlantEntry[]
    | undefined
  const watchedPlants = rawWatchedPlants ?? []
  // 所属 BG の候補 = 同じダイアログの BG 情報で選ばれている BG (重複は 1 つにまとめる)。
  const rawWatchedBgs = useWatch({ control, name: 'bgs' }) as
    | BgEntryOfForm[]
    | undefined
  const bgInfoIds = useMemo(
    () => [
      ...new Set(
        (rawWatchedBgs ?? [])
          .map((b) => b.bgMasterId)
          .filter((id) => id !== ''),
      ),
    ],
    [rawWatchedBgs],
  )
  const bgLabel = (id: string): string => {
    const bg = allBgMasters?.find((b) => b.id === id)
    return bg === undefined ? id : `${bg.bgCode} - ${bg.bgName}`
  }
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | { year: number; month: number; day: number }
    | undefined

  // 行ごとの select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない)。
  const { errors } = useFormState({ control })
  const plantErrors = errors.plants as
    | Array<
        | {
            plantMasterId?: { message?: string }
            bgMasterId?: { message?: string }
          }
        | undefined
      >
    | undefined

  /**
   * area narrow 済み候補 (全行共通、AC-03)。
   */
  const selectedPlantKey = watchedPlants.map((p) => p.plantMasterId).join(',')
  const filteredCandidates = useMemo(() => {
    const selected = new Set(selectedPlantKey.split(','))
    return plantMasters.filter(
      (p) =>
        (selfAreaCode === undefined || p.areaCode === selfAreaCode) &&
        (planCategory === undefined ||
          selected.has(p.id) ||
          plantIsCandidateForPlanCategory({
            todayPowerType: p.powerType,
            plantMasterId: p.id,
            units: plantUnits,
            planCategory,
            today: todayCivilDate(),
          })),
    )
  }, [plantMasters, selfAreaCode, planCategory, plantUnits, selectedPlantKey])

  const noCandidates = filteredCandidates.length === 0

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 追加ボタン (design `.occto-sub-header` 準拠:
          border-top + padding-top + marginTop で直前セクションと区切り、
          add ボタンは size="sm" で他マスタと統一)。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(`page:${i18nNs}.form.plants`)}
        </Text>
        <ButtonTooltip
          content={
            noCandidates ? t(`page:${i18nNs}.form.noPlantCandidates`) : ''
          }
        >
          <TertiaryButton
            disabled={noCandidates}
            onClick={() => {
              void append({
                plantMasterId: '',
                // BG 情報が 1 つだけならそれを既定にする (竹 `AddGenPlanPlant`)
                bgMasterId: bgInfoIds.length === 1 ? (bgInfoIds[0] ?? '') : '',
                validFrom: planMasterRowDefaultValidFrom(
                  watchedRecordValidFrom,
                ),
                validTo: undefined,
              })
            }}
            size="sm"
            title={
              noCandidates ? t(`page:${i18nNs}.form.noPlantCandidates`) : ''
            }
          >
            {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                揃わず、 翻訳のたびに記号を書き写す必要もあった。
                design css `.btn:not(...)>svg` = 20px。 */}
            <FaPlusRegular boxSize={iconSize.labeledButton} />
            {t(`page:${i18nNs}.form.addPlant`)}
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>

      {/* 発電所エントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md">
            {t(`page:${i18nNs}.form.noPlantsRegistered`)}
          </Text>
        )}
        {fields.map((field, idx) => {
          const entry: PlantEntry = watchedPlants[idx] ?? {
            plantMasterId: '',
            bgMasterId: '',
            validFrom: todayCivilDate(),
          }

          // 他の行で継続中 (適用終了が空欄) の系統コードは候補から除く。 その系統コードの行が
          // すべて適用終了で閉じていれば候補に出す (所属 BG の付け替え = 旧い行に適用終了 + 新しい行)。
          // この行自身の選択は自分の select に残る。
          const plantOptsForRow = availableOptions(
            filteredCandidates,
            new Set(
              watchedPlants
                .filter((p, i) => i !== idx && !p.validTo)
                .map((p) => p.plantMasterId)
                .filter((id) => id !== ''),
            ),
            entry.plantMasterId,
          )

          // 所属 BG の候補 = BG 情報の BG。 BG 情報から外した BG を指したままの行は、選択中の表示だけ
          // fallbackLabel で残す (候補には出さない)。
          const bgOptsForRow = bgInfoIds.map((id) => ({
            value: id,
            label: bgLabel(id),
          }))

          const plantError = plantErrors?.[idx]?.plantMasterId
          // 入力中の検査: BG 情報から外した BG を指す行は、保存を押す前から所属 BG 欄にエラーを出す。
          const bgNotInBgs =
            entry.plantMasterId !== '' &&
            entry.bgMasterId !== '' &&
            !bgInfoIds.includes(entry.bgMasterId)
          const bgErrorMessage =
            plantErrors?.[idx]?.bgMasterId?.message ??
            (bgNotInBgs
              ? t(`page:${i18nNs}.serverError.reason.plantBgNotInBgs`, {
                  plant_code:
                    plantMasters.find((p) => p.id === entry.plantMasterId)
                      ?.plantCode ?? entry.plantMasterId,
                })
              : undefined)

          return (
            <Box
              key={field.id}
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* 発電所 select + 所属 BG select + 削除ボタン */}
                <HStack align="flex-start" gap={3}>
                  <Box flex={1} minWidth={0}>
                    <FieldRoot invalid={plantError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.plantLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        aria-label={t(`page:${i18nNs}.form.plantLabel`)}
                        control={control}
                        // 既選択値が opts に無い場合のフォールバック (表示のみ。
                        // 選択肢には出さない)。 未選択 ('') は placeholder に戻す。
                        fallbackLabel={(v) => (v === '' ? undefined : v)}
                        name={`plants.${idx}.plantMasterId`}
                        options={plantOptsForRow.map((p) => ({
                          value: p.id,
                          label: `${p.plantCode} - ${p.plantName}`,
                        }))}
                        placeholder={t(`page:${i18nNs}.form.plantUnselected`)}
                      />
                      {plantError !== undefined && (
                        <FieldErrorText>{plantError.message}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  <Box flex={1} minWidth={0}>
                    <FieldRoot invalid={bgErrorMessage !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.affiliationBgLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        aria-label={t(`page:${i18nNs}.form.affiliationBgLabel`)}
                        control={control}
                        // BG 情報から外した BG を指す行の選択中の表示 (注記なし)。
                        fallbackLabel={(v) =>
                          v === '' ? undefined : bgLabel(v)
                        }
                        name={`plants.${idx}.bgMasterId`}
                        options={bgOptsForRow}
                        placeholder={t(
                          `page:${i18nNs}.form.affiliationBgUnselected`,
                        )}
                      />
                      {bgErrorMessage !== undefined && (
                        <FieldErrorText>{bgErrorMessage}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  {/* 削除ボタンは、隣の欄と同じ「ラベル + 本体」の構造に置く (ラベルの高さの見えない行を
                      上に持つ)。 固定の余白で位置を合わせないので、エラー文で欄の高さが変わっても動かない。 */}
                  <FieldRoot flexShrink={0} width="auto">
                    <Box visibility="hidden" aria-hidden>
                      <SubFormLabel>{' '}</SubFormLabel>
                    </Box>
                    <TertiaryButton
                      aria-label={t(`page:${i18nNs}.form.removePlant`)}
                      colorPalette="red"
                      onClick={() => {
                        remove(idx)
                      }}
                      size="sm"
                    >
                      {t(`page:${i18nNs}.form.removePlant`)}
                    </TertiaryButton>
                  </FieldRoot>
                </HStack>

                {/* 適用期間 */}
                <PeriodFields
                  basePath={`plants.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs={i18nNs}
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}
